// Detalle del cliente (superadmin) — /clientes/$id.
//
// Reemplaza al viejo modal de editar. Concentra TODA la configuración a nivel
// administrador del tenant, organizada en sub-pestañas. Por el modelo
// done-for-you, aquí vive lo técnico: datos generales, correo (Resend),
// chatbot, embudos, estructura del sitio, SLA, integraciones y snippets.
//
// El cliente no entra acá. Correo, chatbot, pipelines, SLA, integraciones
// y snippets los edita solo el superadmin. El contenido del sitio sigue en /cms.

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { configRouteRedirect } from '~/lib/config-access';
import { useMemo, useRef, useState, useEffect, type FormEvent } from 'react';
import { ArrowLeft, LogIn, Save, ShieldAlert, Loader2 } from 'lucide-react';
import { currentUser, pb } from '~/lib/pb';
import { TenantMailSettings } from '~/components/ResendSettings/ResendSettings';
import { TenantSetupChecklist } from '~/components/TenantSetupChecklist/TenantSetupChecklist';
import { TabErrorBoundary } from '~/components/TabErrorBoundary/TabErrorBoundary';
import {
  useTenants,
  useUpdateTenant,
  useUpdateTenantLogo,
  type TenantInput,
} from '~/lib/tenants-queries';
import { useTenantContext, ActiveTenantOverrideProvider } from '~/lib/tenantContext';
import { optimizeBrandLogo } from '~/lib/cms-asset-upload';
import type { Tenant } from '~/lib/types';
import {
  PLAN_LABELS,
  PLAN_ORDER,
  REGION_ORDER,
  REGION_LABELS,
  REGION_CURRENCY,
  TIMEZONE_OPTIONS,
  defaultTimezone,
  ensureCompletoOption,
  planDisplayLabel,
  presetCapabilities,
  tenantHasCapability,
  CUSTOM_PLAN_SLUG,
  type Region,
  type TenantCapabilities,
} from '~/lib/capabilities';
import {
  ACTIVATE_CAPABILITY_LABEL,
  DETAIL_TAB_ALIASES,
  DETAIL_TABS,
  LEGACY_DETAIL_SECTIONS,
  missingTabMessage,
  resolveDetailTab,
  type DetailTabDef,
  type DetailTabId,
} from '~/lib/detail-tabs';
import { CapabilityPicker } from '~/components/CapabilityPicker/CapabilityPicker';
import { useActivePlans, type Plan } from '~/lib/plans-queries';
import { SLA_DEFAULTS, type SlaSettings } from '~/lib/sla';
import { TenantOffboardCard } from '~/components/TenantOffboard/TenantOffboardCard';
import { ChatbotSettings } from '~/components/ChatbotSettings/ChatbotSettings';
import { TenantChatbotAdmin } from '~/components/ChatbotSettings/TenantChatbotAdmin';
import { IntegrationsSettings } from '~/components/IntegrationsSettings/IntegrationsSettings';
import { ShareSettings } from '~/components/ShareSettings/ShareSettings';
import { LeadNoticesSettings } from '~/components/LeadNotices/LeadNoticesSettings';
import { EmailEngagementSettings } from '~/components/EmailEngagementSettings/EmailEngagementSettings';
import { DefaultAssigneeSettings } from '~/components/DefaultAssignee/DefaultAssigneeSettings';
import { CloudflareAnalyticsCard } from '~/components/CloudflareAnalytics/CloudflareAnalyticsCard';
import { PipelinesView } from './_authed.pipelines';
import { SchemasView } from './_authed.schemas';
import { Field, GitHubPatField } from './_authed.clientes';
import styles from './_authed.clientes.module.css';

type DetailRequest =
  | DetailTabId
  | keyof typeof LEGACY_DETAIL_SECTIONS
  | keyof typeof DETAIL_TAB_ALIASES;

const DETAIL_REQUESTS = new Set<string>([
  ...DETAIL_TABS,
  ...Object.keys(LEGACY_DETAIL_SECTIONS),
  ...Object.keys(DETAIL_TAB_ALIASES),
]);

interface DetailSearch {
  tab?: DetailRequest;
}

export const Route = createFileRoute('/_authed/clientes_/$id')({
  beforeLoad: ({ params, search }) => {
    const tab = typeof search.tab === 'string' ? search.tab : undefined;
    const dest = configRouteRedirect(currentUser()?.role, `/clientes/${params.id}`, tab);
    if (dest) throw redirect(dest);
  },
  validateSearch: (s: Record<string, unknown>): DetailSearch => ({
    tab:
      typeof s.tab === 'string' && DETAIL_REQUESTS.has(s.tab)
        ? (s.tab as DetailRequest)
        : undefined,
  }),
  component: TenantDetailPage,
});

function TenantDetailPage() {
  const me = currentUser();
  const isSuperadmin = me?.role === 'superadmin';
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const tenantsQ = useTenants();
  const plansQ = useActivePlans();
  const tenant = useMemo(
    () => (tenantsQ.data ?? []).find((t) => t.id === id) ?? null,
    [tenantsQ.data, id],
  );

  const [tab, setTab] = useState<string>(search.tab ?? 'general');
  useEffect(() => {
    if (search.tab && search.tab !== tab) setTab(search.tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.tab]);

  function goTab(next: DetailTabId) {
    setTab(next);
    navigate({
      to: '/clientes/$id',
      params: { id },
      search: next === 'general' ? {} : { tab: next },
      replace: true,
    });
  }

  const fichaTabs: DetailTabDef[] = [
    { id: 'general', label: 'General', show: true },
    { id: 'implementacion', label: 'Implementación', show: true },
    { id: 'correo', label: 'Correo', show: true },
    { id: 'avisos', label: 'Avisos de leads', show: true },
    { id: 'chatbot', label: 'Chatbot', show: true },
    { id: 'embudos', label: 'Embudos', show: true },
    { id: 'estructura', label: 'Estructura del sitio', show: true },
  ];
  const resolved = resolveDetailTab(tab, fichaTabs);
  const activeTab = resolved.activeId;

  useEffect(() => {
    if (!tenant || !resolved.section) return;
    const sectionId = resolved.section;
    const frame = requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [tenant, resolved.section]);

  const { enterTenantView } = useTenantContext();
  function enterAsTenant() {
    if (!tenant) return;
    enterTenantView(tenant.id);
    void navigate({ to: '/' });
  }

  if (!isSuperadmin) {
    return (
      <div className={styles.page}>
        <div className={styles.notSuperadmin}>
          <ShieldAlert size={28} aria-hidden />
          <p>Esta sección es solo para administradores del sistema.</p>
        </div>
      </div>
    );
  }

  if (tenantsQ.isLoading) {
    return (
      <div className={styles.page}>
        <p className={styles.notice}>Cargando cliente…</p>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className={styles.page}>
        <p className={styles.notice}>
          No encontramos este cliente.{' '}
          <button className="btn btn--ghost" onClick={() => navigate({ to: '/clientes' })}>
            Volver a Clientes
          </button>
        </p>
      </div>
    );
  }

  const planLabel = planDisplayLabel(tenant.plan, plansQ.data);

  return (
    <div className={styles.page}>
      <div className="shell__inner">
        <header className={styles.fichaHeader}>
          <button
            type="button"
            className={`${styles.backBtn} ${styles.fichaBack}`}
            onClick={() => navigate({ to: '/clientes' })}
            aria-label="Volver a Clientes"
          >
            <ArrowLeft size={16} aria-hidden /> Clientes
          </button>
          <div>
            <h1 className={styles.fichaTitle}>{tenant.name}</h1>
            <div className={styles.fichaMeta}>
              <span className={tenant.active ? styles.fichaChipOn : styles.fichaChipOff}>
                {tenant.active ? 'Activo' : 'Suspendido'}
              </span>
              <span className={styles.fichaChip}>{planLabel}</span>
              {tenant.domain ? <span className={styles.fichaSlug}>{tenant.domain}</span> : null}
              <span className={styles.fichaSlug}>{tenant.slug}</span>
            </div>
          </div>
          <div className={styles.fichaActions}>
            <button
              type="button"
              className={styles.button}
              onClick={enterAsTenant}
              disabled={!tenant.active}
              title={
                tenant.active
                  ? 'Ver el panel como lo ve el cliente'
                  : 'El cliente está suspendido. Reactívalo para entrar como él.'
              }
            >
              <LogIn size={14} aria-hidden /> Entrar como cliente
            </button>
            <OffboardDialog tenant={tenant} />
          </div>
        </header>

        <nav className={styles.fichaTabs} role="tablist" aria-label="Configuración del cliente">
          {fichaTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={activeTab === t.id ? styles.fichaTabActive : styles.fichaTab}
              onClick={() => goTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {/* Override del tenant activo: PipelinesView opera sobre ESTE cliente. */}
        <ActiveTenantOverrideProvider tenantId={tenant.id}>
          <div className={styles.detailBody}>
            {resolved.missing && (
              <div className={styles.notice} role="status">
                <p>{missingTabMessage(resolved.missing.label)}</p>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    goTab('general');
                    requestAnimationFrame(() => {
                      document.getElementById('capacidades')?.scrollIntoView({ block: 'start' });
                    });
                  }}
                >
                  {ACTIVATE_CAPABILITY_LABEL}
                </button>
              </div>
            )}
            <TabErrorBoundary key={activeTab}>
              {activeTab === 'general' && (
                <div className={styles.detailStack}>
                  <GeneralTab tenant={tenant} />
                </div>
              )}
              {activeTab === 'implementacion' && <ImplementacionTab tenant={tenant} />}
              {activeTab === 'correo' && <TenantMailSettings tenant={tenant} />}
              {activeTab === 'avisos' && (
                <div className={styles.detailStack}>
                  <DefaultAssigneeSettings tenant={tenant} />
                  <LeadNoticesSettings tenant={tenant} />
                  <EmailEngagementSettings tenant={tenant} />
                </div>
              )}
              {activeTab === 'chatbot' && <ChatbotTab tenant={tenant} />}
              {activeTab === 'embudos' && <EmbudosTab tenant={tenant} />}
              {activeTab === 'estructura' && (
                <div className={styles.detailStack}>
                  <SchemasView tenant={tenant} />
                </div>
              )}
            </TabErrorBoundary>
          </div>
        </ActiveTenantOverrideProvider>
      </div>
    </div>
  );
}

function OffboardDialog({ tenant }: { tenant: Tenant }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !open) return;
    el.setAttribute('closedby', 'any');
    if (!el.open) el.showModal();
    if ('closedBy' in HTMLDialogElement.prototype) return;
    const outside = (event: PointerEvent) => {
      const box = el.getBoundingClientRect();
      return (
        event.clientX < box.left ||
        event.clientX > box.right ||
        event.clientY < box.top ||
        event.clientY > box.bottom
      );
    };
    let pressedOutside = false;
    const onDown = (event: PointerEvent) => {
      pressedOutside = outside(event);
    };
    const onUp = (event: PointerEvent) => {
      if (pressedOutside && outside(event)) el.close();
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
    };
  }, [open]);

  return (
    <>
      <button type="button" className={styles.offboardLink} onClick={() => setOpen(true)}>
        Dar de baja…
      </button>
      <dialog
        ref={ref}
        className={styles.offboardDialog}
        aria-labelledby="offboard-title"
        onClose={() => setOpen(false)}
      >
        {open && (
          <>
            <div className={styles.offboardDialogBar}>
              <button
                type="button"
                className={styles.offboardClose}
                autoFocus
                onClick={() => ref.current?.close()}
              >
                Cerrar
              </button>
            </div>
            <TenantOffboardCard tenant={tenant} inDialog />
          </>
        )}
      </dialog>
    </>
  );
}

// ── Logo de marca del cliente (una vez → agenda y futuros embeds) ──────────

function TenantBrandLogoCard({ tenant }: { tenant: Tenant }) {
  const updateLogo = useUpdateTenantLogo();
  const [preview, setPreview] = useState<string | null>(() => {
    if (!tenant.logo) return null;
    try {
      return pb.files.getURL(tenant, tenant.logo);
    } catch {
      return null;
    }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!tenant.logo) {
      setPreview(null);
      return;
    }
    try {
      setPreview(pb.files.getURL(tenant, tenant.logo));
    } catch {
      setPreview(null);
    }
  }, [tenant.id, tenant.logo, tenant.updated]);

  async function onPick(file: File | null) {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('El logo debe pesar menos de 2 MB.');
      return;
    }
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const optimized = await optimizeBrandLogo(file, { maxDimension: 640, quality: 0.85 });
      const saved = Math.round((1 - optimized.size / file.size) * 100);
      setPreview(URL.createObjectURL(optimized));
      await updateLogo.mutateAsync({ id: tenant.id, logoFile: optimized });
      setInfo(
        optimized.size < file.size
          ? `Logo guardado (WebP, −${Math.max(0, saved)}%).`
          : 'Logo guardado.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el logo');
    } finally {
      setBusy(false);
    }
  }

  async function onClear() {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await updateLogo.mutateAsync({ id: tenant.id, clear: true });
      setPreview(null);
      setInfo('Logo quitado.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo quitar el logo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.detailCard}>
      <div className={styles.detailCardHead}>
        <h3 className={styles.detailCardTitle}>Marca — logo</h3>
        <p className={styles.detailCardSub}>
          Se usa en la agenda pública y otros embeds. Las salas pueden sobreescribirlo. Al subir se
          convierte a WebP (~640 px).
        </p>
      </div>
      <div className={styles.logoRow}>
        {preview ? (
          <img src={preview} alt="" className={styles.logoPreview} />
        ) : (
          <div className={styles.logoPlaceholder}>Sin logo</div>
        )}
        <div className={styles.logoActions}>
          <label
            className={`btn btn--ghost ${busy ? styles.logoBusy : ''}`}
            style={{ cursor: busy ? 'wait' : 'pointer' }}
          >
            {busy ? 'Procesando…' : 'Subir'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml,image/heic,image/heif,image/avif,.heic,.heif,.HEIC,.HEIF"
              hidden
              disabled={busy}
              onChange={(e) => {
                void onPick(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
            />
          </label>
          {preview && (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy}
              onClick={() => void onClear()}
            >
              Quitar
            </button>
          )}
          {info && (
            <span className={styles.logoHint} role="status">
              {info}
            </span>
          )}
          {error && (
            <span className={styles.error} role="alert">
              {error}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

type GeneralForm = {
  name: string;
  slug: string;
  domain: string;
  plan: string;
  region: Region;
  timezone: string;
  capabilities: TenantCapabilities;
};

function generalFormFrom(tenant: Tenant, plans: Plan[]): GeneralForm {
  return {
    name: tenant.name,
    slug: tenant.slug,
    domain: tenant.domain ?? '',
    plan: tenant.plan,
    region: (tenant.region ?? 'CL') as Region,
    timezone: tenant.timezone || defaultTimezone(tenant.region),
    capabilities: tenant.capabilities ?? presetCapabilities(tenant.plan, plans),
  };
}

function GeneralTab({ tenant }: { tenant: Tenant }) {
  const update = useUpdateTenant();
  const plansQ = useActivePlans();
  const plans: Plan[] = plansQ.data;
  const planOptions = ensureCompletoOption(
    plans.length > 0
      ? plans.map((p) => ({ slug: p.slug, label: p.label }))
      : PLAN_ORDER.map((slug) => ({ slug, label: PLAN_LABELS[slug] ?? slug })),
  );

  const [form, setForm] = useState<GeneralForm>(() => generalFormFrom(tenant, plans));

  const selectOptions =
    form.plan === CUSTOM_PLAN_SLUG
      ? [
          { slug: CUSTOM_PLAN_SLUG, label: PLAN_LABELS[CUSTOM_PLAN_SLUG] },
          ...planOptions.filter((p) => p.slug !== CUSTOM_PLAN_SLUG),
        ]
      : planOptions.filter((p) => p.slug !== CUSTOM_PLAN_SLUG);

  useEffect(() => {
    setForm(generalFormFrom(tenant, plans));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id, tenant.updated]);

  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function patch(p: Partial<GeneralForm>) {
    setForm((f) => ({ ...f, ...p }));
    setSaved(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync({
        id: tenant.id,
        name: form.name,
        domain: form.domain,
        plan: form.plan,
        region: form.region,
        timezone: form.timezone,
        capabilities: form.capabilities,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <section className={styles.detailCard}>
          <div className={styles.detailCardHead}>
            <h3 className={styles.detailCardTitle}>Datos del cliente</h3>
            <p className={styles.detailCardSub}>
              Identidad, plan y capacidades del paquete contratado.
            </p>
          </div>
          <div className={styles.detailFields}>
            <Field label="Nombre" required>
              <input
                type="text"
                className={styles.input}
                value={form.name}
                onChange={(e) => patch({ name: e.target.value })}
                required
              />
            </Field>
            <Field
              label="Slug"
              hint="Identificador en URLs y en el sistema. No editable (cambiarlo rompería enlaces y publicación)."
            >
              <input type="text" className={styles.input} value={form.slug} disabled />
            </Field>
            <Field
              label="Dominio (opcional)"
              hint="Solo informativo (sitio del cliente). Ej: misitio.com"
            >
              <input
                type="text"
                className={styles.input}
                value={form.domain ?? ''}
                onChange={(e) => patch({ domain: e.target.value })}
              />
            </Field>
            <div className={styles.row2}>
              <Field
                label="Plan (preset)"
                tip="El plan es solo un preset de capacidades. Si ajustas las capabilities abajo y no coinciden con un preset, el plan pasa a Custom. Re-elige un plan para volver a un preset limpio."
              >
                <select
                  className={styles.select}
                  value={form.plan}
                  onChange={(e) => {
                    const p = e.target.value as TenantInput['plan'];
                    patch({ plan: p, capabilities: presetCapabilities(p, plans) });
                  }}
                >
                  {selectOptions.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Región"
                tip={`La moneda del cliente se deriva de la región y no es editable: ${REGION_CURRENCY[form.region]}. Todos los montos del cliente van en esa moneda.`}
              >
                <select
                  className={styles.select}
                  value={form.region}
                  onChange={(e) => {
                    const r = e.target.value as TenantInput['region'];
                    const wasDefault =
                      !form.timezone || form.timezone === defaultTimezone(form.region);
                    patch(wasDefault ? { region: r, timezone: defaultTimezone(r) } : { region: r });
                  }}
                >
                  {REGION_ORDER.map((r) => (
                    <option key={r} value={r}>
                      {REGION_LABELS[r]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Zona horaria"
                tip="Define a qué hora LOCAL del negocio se envían los recordatorios de tareas. El sistema convierte a la hora real del servidor automáticamente."
              >
                <select
                  className={styles.select}
                  value={form.timezone || defaultTimezone(form.region)}
                  onChange={(e) => patch({ timezone: e.target.value })}
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Estado"
                hint="Se cambia con Dar de baja…, en la cabecera. Guardar este formulario no lo modifica."
              >
                <p className={styles.detailCardSub}>{tenant.active ? 'Activo' : 'Suspendido'}</p>
              </Field>
            </div>
          </div>
          <div className={styles.detailSub} id="capacidades">
            <Field
              label="Capacidades"
              tip="Definen qué ve el cliente. Si las cambias y dejan de coincidir con el plan, el plan pasa a Custom. Completo prende todas."
            >
              <CapabilityPicker
                capabilities={form.capabilities}
                plans={plans}
                onChange={(next) => patch(next)}
              />
            </Field>
          </div>
        </section>

        <TenantBrandLogoCard tenant={tenant} />

        <div className={styles.actionRow}>
          <button type="submit" className={styles.button} disabled={update.isPending}>
            {update.isPending ? (
              <>
                <Loader2 size={14} aria-hidden className={styles.spin} /> Guardando…
              </>
            ) : (
              <>
                <Save size={14} aria-hidden /> Guardar cambios
              </>
            )}
          </button>
          {error ? (
            <span className={styles.error} role="alert">
              {error}
            </span>
          ) : saved ? (
            <span className={styles.savedInline} role="status">
              Guardado
            </span>
          ) : null}
        </div>
      </form>
    </>
  );
}

function ImplementacionTab({ tenant }: { tenant: Tenant }) {
  const hasAnalytics = tenantHasCapability(tenant, 'analytics_traffic');
  const hasAds =
    tenantHasCapability(tenant, 'ads_google') || tenantHasCapability(tenant, 'ads_meta');

  return (
    <div className={styles.detailStack}>
      <TabErrorBoundary>
        <TenantSetupChecklist tenantId={tenant.id} />
      </TabErrorBoundary>
      <nav className={styles.sectionNav} aria-label="Secciones de implementación">
        <a href="#instalar">Claves y snippets</a>
        <a href="#cms">CMS</a>
        <a href="#integraciones">Google Ads y analítica</a>
      </nav>

      <section id="instalar" className={styles.implSection}>
        <h2 className={styles.implTitle}>Claves y snippets</h2>
        <p className={styles.detailCardSub}>
          La clave pk_ y los snippets de formulario y chatbot están siempre acá, aunque el plan no
          tenga esa capacidad. El conocimiento del bot, las etapas y la estructura del sitio están
          en sus pestañas.
        </p>
        <ShareSettings tenant={tenant} />
      </section>

      <section id="cms" className={styles.implSection}>
        <h2 className={styles.implTitle}>CMS — repo y PAT</h2>
        <CmsRepoForm tenant={tenant} />
      </section>

      <section id="integraciones" className={styles.implSection}>
        <h2 className={styles.implTitle}>Google Ads y analítica</h2>
        {!hasAnalytics && !hasAds && (
          <p className={styles.notice}>
            El plan no incluye analítica ni anuncios. Puedes dejarlos listos igual; el cliente los
            ve cuando los actives en General → Capacidades.
          </p>
        )}
        <CloudflareAnalyticsCard tenant={tenant} />
        <AnalyticsIntegrationCard tenant={tenant} />
        <IntegrationsSettings tenant={tenant} />
      </section>
    </div>
  );
}

function ChatbotTab({ tenant }: { tenant: Tenant }) {
  const hasChatbot = tenantHasCapability(tenant, 'chatbot');
  return (
    <div className={styles.detailStack}>
      {!hasChatbot && (
        <p className={styles.notice}>
          Este cliente no tiene Chatbot activo. Puedes dejarlo listo igual. Actívalo en General →
          Capacidades para que lo vea.
        </p>
      )}
      <TenantChatbotAdmin tenant={tenant} />
      <ChatbotSettings tenant={tenant} variant="client" />
    </div>
  );
}

function EmbudosTab({ tenant }: { tenant: Tenant }) {
  const hasCrm = tenantHasCapability(tenant, 'crm');
  return (
    <div className={styles.detailStack}>
      {!hasCrm && (
        <p className={styles.notice}>
          Este cliente no tiene CRM activo. El tablero lo ve cuando lo actives en General →
          Capacidades. Las etapas se pueden dejar listas igual.
        </p>
      )}
      <PipelinesView />
      <div id="sla">
        <SlaTab tenant={tenant} />
      </div>
    </div>
  );
}

function CmsRepoForm({ tenant }: { tenant: Tenant }) {
  const update = useUpdateTenant();
  const [repoUrl, setRepoUrl] = useState(tenant.cms_repo_url ?? '');
  const [branch, setBranch] = useState(tenant.cms_repo_branch ?? '');
  const [pat, setPat] = useState('');
  const [optimize, setOptimize] = useState(tenant.cms_image_optimize !== false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const hasExistingPat = Boolean(
    tenant.cms_repo_url && (tenant.cms_github_token_env || tenant.cms_github_pat),
  );

  useEffect(() => {
    setRepoUrl(tenant.cms_repo_url ?? '');
    setBranch(tenant.cms_repo_branch ?? '');
    setPat('');
    setOptimize(tenant.cms_image_optimize !== false);
    setSaved(false);
    setError(null);
  }, [
    tenant.id,
    tenant.updated,
    tenant.cms_repo_url,
    tenant.cms_repo_branch,
    tenant.cms_image_optimize,
  ]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync({
        id: tenant.id,
        cms_repo_url: repoUrl,
        cms_repo_branch: branch,
        cms_github_pat: pat,
        cms_image_optimize: optimize,
      });
      setPat('');
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <section className={styles.detailCard}>
        <p className={styles.detailCardSub}>
          Repo del sitio y credenciales para el botón Publicar del CMS.
        </p>
        <div className={styles.detailFields}>
          <Field
            label="URL del repo (HTTPS)"
            tip={
              <>
                URL HTTPS del repo en GitHub. Ej:{' '}
                <code>https://github.com/aldeacms/lifefocus-agency.git</code>.
              </>
            }
          >
            <input
              type="url"
              className={styles.input}
              value={repoUrl}
              onChange={(e) => {
                setRepoUrl(e.target.value);
                setSaved(false);
              }}
              placeholder="https://github.com/owner/repo.git"
            />
          </Field>
          <Field label="Branch" hint="Si lo dejas vacío, se usa main.">
            <input
              type="text"
              className={styles.input}
              value={branch}
              onChange={(e) => {
                setBranch(e.target.value);
                setSaved(false);
              }}
              placeholder="main"
            />
          </Field>
          <GitHubPatField
            value={pat}
            onChange={(v) => {
              setPat(v);
              setSaved(false);
            }}
            repoUrl={repoUrl}
            hasExisting={hasExistingPat}
          />
          <Field
            label="Optimización de imágenes"
            tip="Al subir, el CMS achica la imagen a 1400 px y la convierte a WebP."
          >
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={optimize}
                onChange={(e) => {
                  setOptimize(e.target.checked);
                  setSaved(false);
                }}
              />
              <span>Optimizar imágenes automáticamente</span>
            </label>
          </Field>
        </div>
      </section>
      <div className={styles.actionRow}>
        <button type="submit" className={styles.button} disabled={update.isPending}>
          {update.isPending ? (
            <>
              <Loader2 size={14} aria-hidden className={styles.spin} /> Guardando…
            </>
          ) : (
            <>
              <Save size={14} aria-hidden /> Guardar CMS
            </>
          )}
        </button>
        {error ? (
          <span className={styles.error} role="alert">
            {error}
          </span>
        ) : saved ? (
          <span className={styles.savedInline} role="status">
            Guardado
          </span>
        ) : null}
      </div>
    </form>
  );
}

// ── Pestaña SLA: velocidad de primera respuesta ────────────────────────────

function SlaTab({ tenant }: { tenant: Tenant }) {
  const update = useUpdateTenant();
  const initial = (tenant.settings as Record<string, unknown>)?.['sla'] as SlaSettings | undefined;
  const [form, setForm] = useState<SlaSettings>({ ...SLA_DEFAULTS, ...(initial ?? {}) });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cur = (tenant.settings as Record<string, unknown>)?.['sla'] as SlaSettings | undefined;
    setForm({ ...SLA_DEFAULTS, ...(cur ?? {}) });
    setSaved(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id, tenant.updated]);

  function patch(p: Partial<SlaSettings>) {
    setForm((f) => ({ ...f, ...p }));
    setSaved(false);
  }
  function num(v: string, fallback: number): number {
    const n = parseInt(v, 10);
    return isNaN(n) ? fallback : n;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      const nextSettings = {
        ...((tenant.settings as Record<string, unknown>) ?? {}),
        sla: form,
      };
      await update.mutateAsync({ id: tenant.id, settings: nextSettings });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <section className={styles.detailCard}>
        <div className={styles.detailCardHead}>
          <h3 className={styles.detailCardTitle}>Velocidad de respuesta (SLA)</h3>
          <p className={styles.detailCardSub}>
            Avisa cuando una oportunidad nueva lleva demasiado tiempo sin que nadie la responda.
            Responder rápido es lo que más mueve las ventas.
          </p>
        </div>
        <div className={styles.detailFields}>
          <Field
            label="Activar avisos de SLA"
            hint="Si lo apagas, no se generan alertas de respuesta tardía."
          >
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.enabled ?? true}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              <span>Avisar cuando un lead no se atiende a tiempo</span>
            </label>
          </Field>
          <div className={styles.row2}>
            <Field
              label="Primer aviso (minutos)"
              hint="Minutos sin primera respuesta antes de avisar al responsable."
            >
              <input
                type="number"
                min={1}
                className={styles.input}
                value={form.first_response_minutes ?? SLA_DEFAULTS.first_response_minutes}
                onChange={(e) =>
                  patch({
                    first_response_minutes: num(
                      e.target.value,
                      SLA_DEFAULTS.first_response_minutes,
                    ),
                  })
                }
              />
            </Field>
            <Field
              label="Escalar a admins (minutos)"
              hint="Si sigue sin atenderse, se avisa a los administradores del cliente."
            >
              <input
                type="number"
                min={1}
                className={styles.input}
                value={form.escalation_minutes ?? SLA_DEFAULTS.escalation_minutes}
                onChange={(e) =>
                  patch({
                    escalation_minutes: num(e.target.value, SLA_DEFAULTS.escalation_minutes),
                  })
                }
              />
            </Field>
          </div>
          <Field label="Escalación" hint="A quién se avisa en el segundo umbral.">
            <select
              className={styles.select}
              value={form.escalate_to ?? 'admins'}
              onChange={(e) => patch({ escalate_to: e.target.value as SlaSettings['escalate_to'] })}
            >
              <option value="admins">Escalar a los administradores</option>
              <option value="none">No escalar (solo el primer aviso)</option>
            </select>
          </Field>
          <Field
            label="Solo en horario laboral"
            hint="Los avisos solo se envían dentro del horario del negocio (según su zona horaria). Evita avisos de madrugada."
          >
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.business_hours_only ?? true}
                onChange={(e) => patch({ business_hours_only: e.target.checked })}
              />
              <span>Respetar horario laboral</span>
            </label>
          </Field>
          {(form.business_hours_only ?? true) && (
            <div className={styles.row2}>
              <Field label="Hora de inicio" hint="Hora local del negocio (0-23).">
                <input
                  type="number"
                  min={0}
                  max={23}
                  className={styles.input}
                  value={form.business_start_hour ?? SLA_DEFAULTS.business_start_hour}
                  onChange={(e) =>
                    patch({
                      business_start_hour: num(e.target.value, SLA_DEFAULTS.business_start_hour),
                    })
                  }
                />
              </Field>
              <Field label="Hora de término" hint="Hora local del negocio (0-23).">
                <input
                  type="number"
                  min={0}
                  max={23}
                  className={styles.input}
                  value={form.business_end_hour ?? SLA_DEFAULTS.business_end_hour}
                  onChange={(e) =>
                    patch({
                      business_end_hour: num(e.target.value, SLA_DEFAULTS.business_end_hour),
                    })
                  }
                />
              </Field>
            </div>
          )}
        </div>
      </section>

      <div className={styles.actionRow}>
        <button type="submit" className={styles.button} disabled={update.isPending}>
          <Save size={14} aria-hidden /> {update.isPending ? 'Guardando…' : 'Guardar SLA'}
        </button>
        {error ? (
          <span className={styles.error} role="alert">
            {error}
          </span>
        ) : saved ? (
          <span className={styles.savedInline} role="status">
            Guardado
          </span>
        ) : null}
      </div>
    </form>
  );
}

function AnalyticsIntegrationCard({ tenant }: { tenant: Tenant }) {
  const update = useUpdateTenant();
  const readSiteId = () =>
    ((
      (tenant.settings as Record<string, unknown>)?.['plausible'] as
        | Record<string, unknown>
        | undefined
    )?.['site_id'] as string | undefined) ?? '';

  const [siteId, setSiteId] = useState(readSiteId);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSiteId(readSiteId());
    setSaved(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id, tenant.updated]);

  const dirty = siteId.trim() !== readSiteId().trim();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      const settings = { ...((tenant.settings as Record<string, unknown>) ?? {}) };
      const plausible = {
        ...((settings['plausible'] as Record<string, unknown>) ?? {}),
      };
      const v = siteId.trim();
      if (v) plausible['site_id'] = v;
      else delete plausible['site_id'];
      settings['plausible'] = plausible;
      await update.mutateAsync({ id: tenant.id, settings });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <section className={styles.detailCard}>
        <div className={styles.detailCardHead}>
          <h3 className={styles.detailCardTitle}>Analítica web (Plausible)</h3>
          <p className={styles.detailCardSub}>
            Dominio del sitio en Plausible CE (analytics.lifefocus.agency). Sigue alimentando
            Plausible y el script que el publisher inyecta al publicar. Si este cliente tiene
            Cloudflare Web Analytics, el reporte usa Cloudflare. Si cambias el dominio, republica el
            sitio para que el script de Plausible tome efecto.
          </p>
        </div>
        <div className={styles.detailFields}>
          <Field
            label="Dominio en Plausible"
            hint="El dominio del sitio tal como está registrado en Plausible. Ej: lifefocus.agency. Vacío = sin widget de tráfico y sin medición del sitio."
          >
            <input
              type="text"
              className={styles.input}
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="lifefocus.agency"
            />
          </Field>
        </div>
      </section>

      <div className={styles.actionRow}>
        <button type="submit" className={styles.button} disabled={!dirty || update.isPending}>
          <Save size={14} aria-hidden /> {update.isPending ? 'Guardando…' : 'Guardar analítica'}
        </button>
        {error ? (
          <span className={styles.error} role="alert">
            {error}
          </span>
        ) : saved ? (
          <span className={styles.savedInline} role="status">
            Guardado
          </span>
        ) : null}
      </div>
    </form>
  );
}
